import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Telescope, Plus, Trash2, Save, Loader2, AlertTriangle, Check, Eye, EyeOff, Boxes } from 'lucide-react';
import { Switch } from './ui/switch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ResearchTemplateEditor = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mode, setMode] = useState('standard');
  const [metrics, setMetrics] = useState([]);
  const [standardSections, setStandardSections] = useState([]);
  const [maxMetrics, setMaxMetrics] = useState(10);
  const [featureEnabled, setFeatureEnabled] = useState(true);
  const [quota, setQuota] = useState(null);
  const [packs, setPacks] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [res, st, pk] = await Promise.all([
          axios.get(`${API}/research/template`),
          axios.get(`${API}/research/status`),
          axios.get(`${API}/research/template/packs`),
        ]);
        setMode(res.data.mode);
        setMetrics(res.data.metrics || []);
        setStandardSections(res.data.standard_sections || []);
        setMaxMetrics(res.data.max_metrics || 10);
        setFeatureEnabled(st.data.addon_enabled);
        setQuota(st.data.quota);
        setPacks(pk.data.packs || []);
      } catch (e) {
        toast.error('Could not load research template');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const applyPack = (pack) => {
    setMetrics(pack.metrics.slice(0, maxMetrics));
    toast.success(`"${pack.name}" pack loaded — tweak the metrics, then save`);
  };

  const toggleVisibility = async (enabled) => {
    setFeatureEnabled(enabled);
    try {
      await axios.post(`${API}/research/addon`, { enabled });
      toast.success(enabled ? 'Prospect Research is now visible to your team' : 'Prospect Research hidden for your organisation');
    } catch (e) {
      setFeatureEnabled(!enabled);
      toast.error(e.response?.data?.detail || 'Failed to update');
    }
  };

  const addMetric = () => {
    if (metrics.length >= maxMetrics) return;
    setMetrics([...metrics, { name: '', description: '' }]);
  };

  const updateMetric = (i, field, value) => {
    const next = [...metrics];
    next[i] = { ...next[i], [field]: value };
    setMetrics(next);
  };

  const removeMetric = (i) => setMetrics(metrics.filter((_, idx) => idx !== i));

  const save = async () => {
    if (mode === 'custom') {
      const valid = metrics.filter((m) => m.name.trim() && m.description.trim());
      if (valid.length === 0) {
        toast.error('Add at least one metric with a name and description');
        return;
      }
    }
    setSaving(true);
    try {
      const res = await axios.put(`${API}/research/template`, {
        mode,
        metrics: mode === 'custom' ? metrics.filter((m) => m.name.trim() && m.description.trim()) : [],
      });
      setMetrics(res.data.metrics || metrics);
      toast.success(mode === 'custom' ? 'Custom research template saved' : 'Switched to the standard template');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-zinc-400" /></div>;
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-testid="research-template-editor">
      <div className="lg:col-span-2 space-y-4">
        <Card className="border-zinc-200 shadow-none rounded-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Telescope className="w-4 h-4 text-brand-primary" />
              Prospect Research Template
            </CardTitle>
            <CardDescription>
              Choose what PipeGuide researches before your reps' calls. The standard template covers the essentials, or define up to {maxMetrics} of your own research metrics.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                onClick={() => setMode('standard')}
                className={`text-left p-4 rounded-xl border-2 transition-colors ${mode === 'standard' ? 'border-brand-primary bg-brand-primary/5' : 'border-zinc-200 hover:border-zinc-300'}`}
                data-testid="template-mode-standard"
              >
                <div className="flex items-center gap-2 font-semibold text-sm text-zinc-800">
                  {mode === 'standard' && <Check className="w-4 h-4 text-brand-primary" />}
                  Standard template
                </div>
                <p className="text-xs text-zinc-500 mt-1">The full PipeGuide business profile, incl. product-fit mapping to your catalogue.</p>
              </button>
              <button
                onClick={() => setMode('custom')}
                className={`text-left p-4 rounded-xl border-2 transition-colors ${mode === 'custom' ? 'border-brand-primary bg-brand-primary/5' : 'border-zinc-200 hover:border-zinc-300'}`}
                data-testid="template-mode-custom"
              >
                <div className="flex items-center gap-2 font-semibold text-sm text-zinc-800">
                  {mode === 'custom' && <Check className="w-4 h-4 text-brand-primary" />}
                  Custom metrics
                </div>
                <p className="text-xs text-zinc-500 mt-1">You decide the data points — describe each one and the AI fetches it from public sources.</p>
              </button>
            </div>

            {mode === 'custom' && (
              <div className="space-y-3" data-testid="custom-metrics-list">
                {packs.length > 0 && (
                  <div className="bg-zinc-50 border border-zinc-100 rounded-xl p-3" data-testid="template-packs-row">
                    <div className="flex items-center gap-1.5 text-xs font-semibold text-zinc-600 mb-2">
                      <Boxes className="w-3.5 h-3.5 text-brand-primary" />
                      Start from a ready-made pack
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {packs.map((pk) => (
                        <button
                          key={pk.id}
                          onClick={() => applyPack(pk)}
                          className="text-xs px-2.5 py-1.5 rounded-full border border-zinc-200 bg-white text-zinc-700 hover:border-brand-primary hover:text-brand-primary transition-colors"
                          data-testid={`template-pack-${pk.id}`}
                        >
                          {pk.name} <span className="text-zinc-400">· {pk.metrics.length}</span>
                        </button>
                      ))}
                    </div>
                    <p className="text-[11px] text-zinc-400 mt-2">Loading a pack replaces the metrics below — edit freely before saving.</p>
                  </div>
                )}
                {metrics.length === 0 && (
                  <p className="text-sm text-zinc-500">No metrics yet — pick a pack above or add your first one below.</p>
                )}
                {metrics.map((m, i) => (
                  <div key={i} className="flex gap-2 items-start bg-zinc-50 rounded-xl p-3" data-testid={`custom-metric-row-${i}`}>
                    <div className="flex-1 space-y-2">
                      <Input
                        placeholder={`Metric ${i + 1} name (e.g. "Fleet size", "Tech stack", "Tender activity")`}
                        value={m.name}
                        maxLength={60}
                        onChange={(e) => updateMetric(i, 'name', e.target.value)}
                        className="h-9 bg-white"
                        data-testid={`metric-name-input-${i}`}
                      />
                      <Textarea
                        placeholder="Describe for the AI what to look for, e.g. 'How many delivery vehicles or depots they operate, and whether they mention expanding routes.'"
                        value={m.description}
                        maxLength={300}
                        rows={2}
                        onChange={(e) => updateMetric(i, 'description', e.target.value)}
                        className="bg-white text-sm"
                        data-testid={`metric-desc-input-${i}`}
                      />
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => removeMetric(i)} className="text-zinc-400 hover:text-red-500 mt-1" data-testid={`metric-remove-btn-${i}`}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  variant="outline"
                  onClick={addMetric}
                  disabled={metrics.length >= maxMetrics}
                  className="rounded-lg"
                  data-testid="add-metric-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Add metric ({metrics.length}/{maxMetrics})
                </Button>
              </div>
            )}

            <Button onClick={save} disabled={saving} className="bg-brand-primary hover:bg-brand-hover rounded-lg" data-testid="save-template-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save template
            </Button>
          </CardContent>
        </Card>
      </div>

      <div className="space-y-4">
        <Card className="border-zinc-200 shadow-none rounded-md" data-testid="research-visibility-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              {featureEnabled ? <Eye className="w-4 h-4 text-brand-primary" /> : <EyeOff className="w-4 h-4 text-zinc-400" />}
              Feature visibility
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between gap-3">
              <p className="text-xs text-zinc-500 leading-relaxed">
                Research is included in your meeting tokens — each user gets 1 research per token in their monthly allowance{quota ? ` (${quota}/month on your plan)` : ''}. Hide it if your team doesn't use it.
              </p>
              <Switch checked={featureEnabled} onCheckedChange={toggleVisibility} data-testid="research-visibility-switch" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-zinc-200 shadow-none rounded-md">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">What the standard template covers</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-1.5">
              {standardSections.map((s) => (
                <Badge key={s} variant="outline" className="text-xs font-normal text-zinc-600">{s}</Badge>
              ))}
            </div>
            <p className="text-xs text-zinc-400 mt-3">Custom templates keep the Company Snapshot, meeting-contact profile, discovery questions and talking points — your metrics replace the analytical sections in between.</p>
          </CardContent>
        </Card>

        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-2.5" data-testid="template-ai-disclaimer">
          <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
          <p className="text-xs text-amber-800 leading-relaxed">
            Research is AI-generated from a limited set of public sources (max 5 per research) and is <strong>indicative only</strong>. AI can make errors or miss context — your reps are reminded to verify key facts before every call. Metrics the sources don't cover will return "Not publicly available" rather than guesses.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResearchTemplateEditor;
